import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { UiModeProvider } from "@/contexts/UiModeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import SmartSetup from "@/pages/SmartSetup";
import FlowDiagram from "@/pages/FlowDiagram";
import Calculator from "@/pages/Calculator";
import LiquidValue from "@/pages/LiquidValue";
import CompensationPlan from "@/pages/CompensationPlan";
import Ask from "@/pages/Ask";
import Login from "@/pages/Login";
import PendingApproval from "@/pages/PendingApproval";
import Dashboard from "@/pages/Dashboard";
import Wallets from "@/pages/Wallets";
import WalletSweepPage from "@/pages/WalletSweepPage";
import PreDeployCheck from "@/pages/PreDeployCheck";
import NotReporting from "@/pages/NotReporting";
import WalletSync from "@/pages/WalletSync";
import OrgChart from "@/pages/OrgChart";
import Withdrawals from "@/pages/Withdrawals";
import RiskAlerts from "@/pages/RiskAlerts";
import Activate from "@/pages/Activate";
import ResetPassword from "@/pages/ResetPassword";
import AdminMembers from "@/pages/AdminMembers";
import SelectWallet from "@/pages/SelectWallet";
import GettingStarted from "@/pages/GettingStarted";
import OnboardingV2 from "@/pages/OnboardingV2";
import Discover from "@/pages/Discover";
import SizzleToFunnel from "@/pages/SizzleToFunnel";
import GoLink from "@/pages/GoLink";
import AdminTrainees from "@/pages/AdminTrainees";
import OperationMomentum from "@/pages/OperationMomentum";
import AdminShareLinks from "@/pages/AdminShareLinks";
import AdminActivity from "@/pages/AdminActivity";
import AdminEmailLog from "@/pages/AdminEmailLog";
import AdminTrace from "@/pages/AdminTrace";
import AdminPartners from "@/pages/AdminPartners";
import AdminRpa from "@/pages/AdminRpa";
import AdminPartnerUsers from "@/pages/AdminPartnerUsers";
import PartnerConsole from "@/pages/PartnerConsole";
import PartnerPortal from "@/pages/PartnerPortal";
import PartnerClientLogin from "@/pages/PartnerClientLogin";
import ClientStatement from "@/pages/ClientStatement";
import ClientPortal from "@/pages/ClientPortal";
import Academy from "@/pages/Academy";
import MyAcademy from "@/pages/MyAcademy";
import MyTeam from "@/pages/MyTeam";
import OnboardingVideo from "@/components/OnboardingVideo";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { Toaster } from "@/components/ui/sonner";

function ReferralRedirect() {
  const slug = window.location.pathname.split("/r/")[1] || "";
  return <Navigate to={`/?ref=${slug}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <UiModeProvider>
      <div className="mesh-bg" />
      <BrowserRouter>
        <ImpersonationBanner />
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public — pitch + auth flow only */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/r/:slug" element={<ReferralRedirect />} />
          <Route path="/start" element={<Navigate to={`/join${window.location.search}`} replace />} />
          <Route path="/join" element={<OnboardingV2 />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/watch" element={<SizzleToFunnel />} />
          <Route path="/g/:key" element={<GoLink />} />
          <Route path="/sync" element={<WalletSync />} />
          <Route path="/onboarding-v2" element={<OnboardingV2 />} />
          <Route path="/academy" element={<Academy />} />
          <Route path="/p/:slug" element={<PartnerPortal />} />
          <Route path="/p/:slug/login" element={<PartnerClientLogin />} />
          <Route path="/p/:slug/portal" element={<ProtectedRoute role="partner_client"><ClientPortal /></ProtectedRoute>} />
          <Route path="/s/:token" element={<ClientStatement />} />
          <Route path="/video-preview" element={
            <div className="min-h-screen flex items-center justify-center p-4 md:p-10">
              <div className="w-full max-w-3xl">
                <h1 className="font-display text-xl font-semibold text-ink mb-4 text-center">Onboarding Video — Preview</h1>
                <OnboardingVideo />
              </div>
            </div>
          } />

          {/* Authenticated — new-member onboarding gate */}
          <Route path="/getting-started" element={<ProtectedRoute><GettingStarted /></ProtectedRoute>} />

          {/* Authenticated — app */}
          <Route path="/select-wallet" element={<ProtectedRoute><SelectWallet /></ProtectedRoute>} />
          <Route path="/flow" element={<ProtectedRoute><FlowDiagram /></ProtectedRoute>} />
          <Route path="/calculator" element={<ProtectedRoute><Calculator /></ProtectedRoute>} />
          <Route path="/liquid-value" element={<ProtectedRoute><LiquidValue /></ProtectedRoute>} />
          <Route path="/compensation" element={<ProtectedRoute><CompensationPlan /></ProtectedRoute>} />
          <Route path="/ask" element={<ProtectedRoute><Ask /></ProtectedRoute>} />
          <Route path="/setup" element={<ProtectedRoute><SmartSetup /></ProtectedRoute>} />

          {/* Authenticated — Dashboard + Finances */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/wallets" element={<ProtectedRoute><Wallets /></ProtectedRoute>} />
          <Route path="/wallet-sweep" element={<ProtectedRoute role="admin"><WalletSweepPage /></ProtectedRoute>} />
          <Route path="/pre-deploy-check" element={<ProtectedRoute role="admin"><PreDeployCheck /></ProtectedRoute>} />
          <Route path="/admin/not-reporting" element={<ProtectedRoute role="admin"><NotReporting /></ProtectedRoute>} />
          <Route path="/withdrawals" element={<ProtectedRoute><Withdrawals /></ProtectedRoute>} />
          <Route path="/risk" element={<ProtectedRoute><RiskAlerts /></ProtectedRoute>} />

          {/* Authenticated — Admin / Affiliate Manager */}
          <Route path="/crm" element={<Navigate to="/org-chart" replace />} />
          <Route path="/org-chart" element={<ProtectedRoute><OrgChart /></ProtectedRoute>} />
          <Route path="/admin/members" element={<ProtectedRoute role="admin"><AdminMembers /></ProtectedRoute>} />
          <Route path="/admin/academy" element={<ProtectedRoute role="admin"><AdminTrainees /></ProtectedRoute>} />
          <Route path="/admin/onboarding-approvals" element={<ProtectedRoute role="admin"><AdminTrainees /></ProtectedRoute>} />
          <Route path="/admin/trainees" element={<ProtectedRoute role="admin"><AdminTrainees /></ProtectedRoute>} />
          <Route path="/operation-momentum" element={<ProtectedRoute perm="momentum"><OperationMomentum /></ProtectedRoute>} />
          <Route path="/admin/share-links" element={<ProtectedRoute role="admin" perm="marketing"><AdminShareLinks /></ProtectedRoute>} />
          <Route path="/admin/activity" element={<ProtectedRoute role="admin"><AdminActivity /></ProtectedRoute>} />
          <Route path="/admin/trace" element={<ProtectedRoute role="admin"><AdminTrace /></ProtectedRoute>} />
          <Route path="/admin/email-log" element={<ProtectedRoute role="admin"><AdminEmailLog /></ProtectedRoute>} />
          <Route path="/admin/partners" element={<ProtectedRoute role="admin" perm="super"><AdminPartners /></ProtectedRoute>} />
          <Route path="/admin/rpa" element={<ProtectedRoute role="admin" perm="super"><AdminRpa /></ProtectedRoute>} />
          <Route path="/admin/partners/users" element={<ProtectedRoute role="admin" perm="super"><AdminPartnerUsers /></ProtectedRoute>} />
          <Route path="/admin/users" element={<Navigate to="/admin/partners/users" replace />} />
          <Route path="/client" element={<ProtectedRoute role="partner_client"><ClientPortal /></ProtectedRoute>} />

          {/* White-label partner organisations — their own console, nothing else */}
          <Route path="/partner" element={<ProtectedRoute><PartnerConsole /></ProtectedRoute>} />
          <Route path="/my-academy" element={<ProtectedRoute><MyAcademy /></ProtectedRoute>} />
          <Route path="/my-team" element={<ProtectedRoute><MyTeam /></ProtectedRoute>} />

          {/* Legacy redirects — Control Tower / Revenue / Portfolios consolidated into Dashboard */}
          <Route path="/control-tower" element={<Navigate to="/dashboard" replace />} />
          <Route path="/revenue" element={<Navigate to="/dashboard" replace />} />
          <Route path="/portfolios" element={<Navigate to="/dashboard" replace />} />
          <Route path="/live-finances" element={<Navigate to="/dashboard" replace />} />
          <Route path="/mining" element={<Navigate to="/dashboard" replace />} />
          <Route path="/staking" element={<Navigate to="/dashboard" replace />} />
          <Route path="/portfolio" element={<Navigate to="/wallets" replace />} />
          <Route path="/members" element={<Navigate to="/org-chart" replace />} />
          <Route path="/team" element={<Navigate to="/org-chart" replace />} />
          <Route path="/sub-leaders" element={<Navigate to="/org-chart" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </UiModeProvider>
    </AuthProvider>
  );
}

export default App;
